import { cn } from '@/shared/lib/cn';
import { forwardRef, type ComponentPropsWithoutRef, type Ref } from 'react';

const variantClasses: Record<TypographyVariant, string> = {
  body: 'text-[14px] leading-[22px]',
  h1: 'text-[38px] leading-[46px]',
  h2: 'text-[30px] leading-[40px]',
  h3: 'text-[24px] leading-[32px]',
  h4: 'text-[20px] leading-[28px]',
  h5: 'text-[16px] leading-[24px]',
  'footnote-description': 'text-[12px] font-normal leading-[20px]',
};

const weightClasses: Record<TypographyWeight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  bold: 'font-bold',
};

export type TypographyTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'span' | 'p';
export type TypographyWeight = 'regular' | 'medium' | 'bold';
export type TypographyVariant =
  | 'body'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'footnote-description';

type TypographyProps<T extends TypographyTag = 'p'> = {
  as?: T;
  variant?: TypographyVariant;
  weight?: TypographyWeight;
  noWrap?: boolean;
} & ComponentPropsWithoutRef<T>;

export const Typography = forwardRef(
  <T extends TypographyTag = 'p'>(
    {
      variant = 'body',
      weight = 'regular',
      noWrap = false,
      as,
      className,
      children,
      title,
      ...props
    }: TypographyProps<T>,
    ref: Ref<HTMLParagraphElement>
  ) => {
    const Component = as || 'p';

    const isTruncated =
      noWrap ||
      (typeof className === 'string' && className.includes('truncate'));
    const autoTitle =
      isTruncated &&
      title === undefined &&
      (typeof children === 'string' || typeof children === 'number')
        ? String(children)
        : title;

    return (
      <Component
        ref={ref}
        title={autoTitle}
        className={cn(
          weightClasses[weight],
          variantClasses[variant],
          noWrap && 'overflow-hidden text-ellipsis whitespace-nowrap',
          `font-${weight}`,
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Typography.displayName = 'Typography';
